import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IAISearchHistoryResponse, ISearchInfo } from '../types';

interface SearchSliceState {
  activeSessionId: string;
  searchHistory: ISearchInfo[];
  totalCount: number;
  deletingItems: string[];
  newSessionIds: string[];
  isLoading: boolean;
  hasConceptsChanged: boolean;
  hasCompoundsChanged: boolean;
}

const initialState: SearchSliceState = {
  activeSessionId: '',
  searchHistory: [],
  totalCount: 0,
  deletingItems: [],
  newSessionIds: [],
  isLoading: false,
  hasConceptsChanged: false,
  hasCompoundsChanged: false,
};

const searchSliceV2 = createSlice({
  name: 'searchSliceV2',
  initialState,
  reducers: {
    setActiveSessionId: (state, action: PayloadAction<string>) => {
      state.activeSessionId = action.payload;
    },
    setSearchHistory: (state, action: PayloadAction<IAISearchHistoryResponse>) => {
      state.searchHistory = action.payload.searchHistory;
      state.totalCount = action.payload.totalCount;
    },
    appendSearchHistory: (state, action: PayloadAction<IAISearchHistoryResponse>) => {
      const existingIds = new Set(state.searchHistory.map(item => item.searchId));
      const newItems = action.payload.searchHistory.filter(item => !existingIds.has(item.searchId));
      state.searchHistory = [...state.searchHistory, ...newItems];
      state.totalCount = action.payload.totalCount;
    },
    addNewSessionId: (state, action: PayloadAction<string>) => {
      if (!state.newSessionIds.includes(action.payload)) {
        state.newSessionIds.push(action.payload);
      }
    },
    removeNewSessionId: (state, action: PayloadAction<string>) => {
      state.newSessionIds = state.newSessionIds.filter(id => id !== action.payload);
    },
    addDeletingItem: (state, action: PayloadAction<string>) => {
      if (!state.deletingItems.includes(action.payload)) {
        state.deletingItems.push(action.payload);
      }
    },
    removeDeletingItem: (state, action: PayloadAction<string>) => {
      state.deletingItems = state.deletingItems.filter(id => id !== action.payload);
    },
    setIsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    resetSearchState: state => {
      state.activeSessionId = '';
    },
    resetChanges: state => {
      state.hasConceptsChanged = false;
      state.hasCompoundsChanged = false;
    },
    setConceptsChanged: (state, action: PayloadAction<boolean>) => {
      state.hasConceptsChanged = action.payload;
    },
    setCompoundsChanged: (state, action: PayloadAction<boolean>) => {
      state.hasCompoundsChanged = action.payload;
    },
  },
});

export const {
  setActiveSessionId,
  setSearchHistory,
  appendSearchHistory,
  addNewSessionId,

  addDeletingItem,
  removeDeletingItem,
  setIsLoading,
  resetSearchState,
  resetChanges,
  setConceptsChanged,
  setCompoundsChanged,
} = searchSliceV2.actions;

export default searchSliceV2.reducer;
